import Button from '@strava/ui/Button';
import { ModalActions } from '@strava/ui/Modal';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { devLog } from 'utils/dev-utils';
import { ALERT_TYPES } from 'constants/alerts/alert-types';
import Spinner from '@strava/ui/Spinner';
import { isEmpty, isNil } from 'lodash-es';
import {
  CONSENTS_FLOW,
  INPUT_IDS,
  REGISTRATION_FLOW
} from '../../shared/onboarding-constants';
import {
  CLEAR_ALERT,
  ADD_ALERT,
  UPDATE_STEP
} from '../../state/Onboarding.actions';
import { useOnboardingContext } from '../../state/OnboardingContext';
import { stepToUrl, t } from '../../shared/onboarding-utils';
import styles from './NextStepButton.scss';
import { completeProfile } from '../../../../api/onboarding/onboarding';

const NextStepButton = () => {
  const history = useHistory();

  const { state, dispatch } = useOnboardingContext();

  const { page, currentStep, currentAthlete } = state;
  const { firstName, lastName } = currentAthlete;

  const [isProcessing, setIsProcessing] = useState(false);

  const dispatchAlert = (severity, message, target) => {
    dispatch({
      type: ADD_ALERT,
      payload: {
        severity,
        message,
        target
      }
    });
    setIsProcessing(false);
  };

  /**
   * Validates controls on `Create your Profile` step and determines the
   * next step when all values are valid.
   *
   * @returns {string} -next step or null when an error occurs
   */
  const handleCompleteProfile = async () => {
    if (isEmpty(firstName)) {
      dispatchAlert(
        ALERT_TYPES.error,
        t('create_your_profile.errors.first_name'),
        INPUT_IDS.firstName
      );
      return null;
    }
    if (isEmpty(lastName)) {
      dispatchAlert(
        ALERT_TYPES.error,
        t('create_your_profile.errors.last_name'),
        INPUT_IDS.lastName
      );
      return null;
    }
    try {
      const response = await completeProfile({
        data: {
          firstname: firstName,
          lastname: lastName,
          gender: 'man', // TODO: ACTN-315
          dateofbirth: {
            year: '1990', // TODO: ACTN-316
            month: '10',
            day: '22'
          }
        }
      });
      return response.nextStep;
    } catch (e) {
      devLog(e);
      dispatchAlert(ALERT_TYPES.error, t('errors.generic'));
      return null;
    }
  };

  const handleNextStep = async () => {
    dispatch({ type: CLEAR_ALERT });
    setIsProcessing(true);
    let nextStep;

    switch (currentStep) {
      // Registration Flow
      case REGISTRATION_FLOW.createProfile: {
        // possible next step - suggested_friends, facebook_friends
        nextStep = await handleCompleteProfile();
        break;
      }
      case REGISTRATION_FLOW.suggestedFriends: {
        nextStep = REGISTRATION_FLOW.facebookFriends;
        break;
      }
      case REGISTRATION_FLOW.facebookFriends: {
        nextStep = REGISTRATION_FLOW.welcome;
        break;
      }

      // Consents Flow
      case CONSENTS_FLOW.welcome: {
        nextStep = CONSENTS_FLOW.termsOfService;
        break;
      }

      // Registration and Consents flow end
      default: {
        // This page refresh is necessary when the athlete hits the back button -
        // rails controller endpoints are triggered to re-route the user back to `onboarding/continue`.
        // Without it, the back button will just pop the url from react-router and
        // update the address but it will not re-open the modal.
        window.location.href = '/onboarding';
        return;
      }
    }

    if (!isNil(nextStep)) {
      // nextStep is nil when validation fails or an error occurs
      dispatch({
        type: UPDATE_STEP,
        payload: { nextStep }
      });
      history.push(stepToUrl({ page, step: nextStep }));
    }

    setIsProcessing(false);
    devLog(`[NextStepButton] #handleNextStep currentStep - ${nextStep}`);
  };

  // TODO: [react-onboarding] change the button CTA and colors based on which step the
  // athlete is on
  const ctaBtnConfig = {
    variant: 'primary',
    label: 'Continue'
  };

  return (
    <ModalActions className={styles.modalActions}>
      <Button
        className={styles.ctaButton}
        variant={ctaBtnConfig.variant}
        onClick={handleNextStep}
        type="button"
        data-cy="onboarding-next-step-btn"
        disabled={isProcessing}
      >
        {isProcessing ? <Spinner /> : ctaBtnConfig.label}
      </Button>
    </ModalActions>
  );
};

export default NextStepButton;
